import { useEffect, useState } from "react";
import { useNas } from "../context/NasContext";
import {
  DEFAULT_POSTGREST_URL,
  getPostgrestUrl,
  setPostgrestUrl as persistPostgrestUrl,
  getTheme,
  setTheme,
  AppTheme
} from "../settings";

type UpdateState = {
  status:
    | "idle"
    | "checking"
    | "available"
    | "downloading"
    | "downloaded"
    | "none"
    | "skipped"
    | "warning"
    | "error";
  message?: string;
  percent?: number;
};

type RuntimeInfo = {
  success: boolean;
  error?: string;
  runtime?: {
    runtimeDir?: string;
    wheelhouseDir?: string;
    wheelhouseExists?: boolean;
    wheelhouseVersion?: string | null;
    venvPath?: string;
    venvPython?: string | null;
    pythonBinary?: string | null;
    pythonVersion?: string | null;
    pythonSource?: string | null;
    packages?: string[];
    packageStatus?: Record<string, string>;
    usedWheelhouse?: boolean;
    usedVendorFallback?: boolean;
    venvRebuilt?: boolean;
  };
};

const SETTINGS_TABS = [
  { id: "nas", label: "NAS" },
  { id: "api", label: "API" },
  { id: "appearance", label: "Appearance" },
  { id: "runtime", label: "Runtime" },
  { id: "updates", label: "Updates" },
  { id: "notes", label: "Notes" }
] as const;

const normalizeUpdateStatus = (status?: string): UpdateState["status"] | null => {
  if (!status) return null;
  if (status === "done") return "none";
  if (status === "none") return "none";
  if (status === "checking") return "checking";
  if (status === "available") return "available";
  if (status === "downloading") return "downloading";
  if (status === "downloaded") return "downloaded";
  if (status === "skipped") return "skipped";
  if (status === "warning") return "warning";
  if (status === "error") return "error";
  return "idle";
};

const formatPackageStatus = (status?: string) => {
  if (status === "installed-wheelhouse") return "Installed (wheelhouse)";
  if (status === "installed-vendor") return "Installed (vendor)";
  if (status === "exists") return "Already installed";
  return status || "Unknown";
};

const formatBool = (value?: boolean) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Unknown";
};

export default function SettingsPage() {
  const { config, setConfig, triggerMount, status, error, mountPoint } = useNas();
  const [form, setForm] = useState(config);
  const [busy, setBusy] = useState(false);
  const [postgrestUrl, setPostgrestUrl] = useState(getPostgrestUrl());
  const [theme, setThemeState] = useState<AppTheme>(getTheme());
  const [testingApi, setTestingApi] = useState(false);
  const [testResult, setTestResult] = useState<{ status: "ok" | "error"; message: string } | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<(typeof SETTINGS_TABS)[number]["id"]>("nas");
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [updateState, setUpdateState] = useState<UpdateState>({ status: "idle" });
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [runtimeInfo, setRuntimeInfo] = useState<RuntimeInfo | null>(null);
  const [loadingRuntime, setLoadingRuntime] = useState(false);

  const missing = !form.mountPoint;
  const updatesSupported = Boolean(window.api?.checkForUpdates);
  const runtimeSupported = Boolean(window.api?.getStartupRuntime);

  useEffect(() => {
    if (!window.api?.getAppVersion) return;
    window.api
      .getAppVersion()
      .then((version) => setAppVersion(version))
      .catch(() => {
        /* ignore */
      });
  }, []);

  useEffect(() => {
    if (!window.api?.onUpdateProgress) return;
    return window.api.onUpdateProgress((_event, data) => {
      setUpdateState((prev) => ({
        status: normalizeUpdateStatus(data.status) ?? prev.status,
        message: data.message ?? prev.message,
        percent: typeof data.percent === "number" ? data.percent : prev.percent
      }));
    });
  }, []);

  const loadRuntimeInfo = async () => {
    if (!runtimeSupported || !window.api?.getStartupRuntime) {
      setRuntimeInfo(null);
      return;
    }
    setLoadingRuntime(true);
    try {
      const info = await window.api.getStartupRuntime();
      setRuntimeInfo(info as RuntimeInfo | null);
    } catch (err) {
      setRuntimeInfo({
        success: false,
        error: err instanceof Error ? err.message : "Failed to load runtime info."
      });
    } finally {
      setLoadingRuntime(false);
    }
  };

  useEffect(() => {
    void loadRuntimeInfo();
  }, [runtimeSupported]);

  const handleSave = async () => {
    setBusy(true);
    setConfig(form);
    await triggerMount(form).catch(() => {
      /* errors surface in context */
    });
    setBusy(false);
  };

  const handleClear = () => {
    const cleared = { username: "", password: "", serverPath: "", mountPoint: "" };
    setForm(cleared);
    setConfig(cleared);
  };

  const handleSavePostgrest = () => {
    const cleaned = (postgrestUrl || "").trim().replace(/\/+$/, "") || DEFAULT_POSTGREST_URL;
    persistPostgrestUrl(cleaned);
    setPostgrestUrl(cleaned);
    setTestResult({ status: "ok", message: "Saved locally. New requests will use this URL." });
  };

  const handleThemeChange = (nextTheme: AppTheme) => {
    setThemeState(nextTheme);
    setTheme(nextTheme);
  };

  const handleTestConnection = async () => {
    const cleaned = (postgrestUrl || "").trim().replace(/\/+$/, "") || DEFAULT_POSTGREST_URL;
    setPostgrestUrl(cleaned);
    setTestingApi(true);
    setTestResult(null);
    const target = `${cleaned}/`;
    try {
      const response = await fetch(target, { method: "GET" });
      const text = await response.text();
      const preview = text.slice(0, 120).replace(/\s+/g, " ").trim();
      if (response.ok && response.status === 200) {
        setTestResult({
          status: "ok",
          message: "Connection OK"
        });
      } else if (response.ok) {
        setTestResult({
          status: "ok",
          message: preview ? `Success (${response.status}): ${preview}` : `Success (${response.status})`
        });
      } else {
        setTestResult({
          status: "error",
          message: preview
            ? `Failed (${response.status}): ${preview}`
            : `Failed (${response.status})`
        });
      }
    } catch (err) {
      setTestResult({
        status: "error",
        message: err instanceof Error ? err.message : "Unknown error"
      });
    } finally {
      setTestingApi(false);
    }
  };

  const handleCheckUpdates = async () => {
    if (!updatesSupported || !window.api?.checkForUpdates) {
      setUpdateState({
        status: "warning",
        message: "Update checks are available in the desktop app only."
      });
      return;
    }

    setCheckingUpdates(true);
    setUpdateState({ status: "checking", message: "Checking for updates..." });
    try {
      const result = await window.api.checkForUpdates();
      const status = normalizeUpdateStatus(result?.status) ?? "idle";
      let message = "";
      if (status === "downloaded") {
        message = `Update downloaded (${result?.info?.version || "new"}). Ready to install.`;
      } else if (status === "none") {
        message = "No updates available.";
      } else if (status === "skipped") {
        const reason = result?.reason ? String(result.reason) : "updates disabled";
        if (reason === "updates disabled") {
          message = "Update check skipped (updates disabled). Set ENABLE_AUTO_UPDATE=1 to enable.";
        } else {
          message = `Update check skipped (${reason}).`;
        }
      } else if (status === "warning" || status === "error") {
        message = "Update check failed. Verify the update server.";
      }
      setUpdateState((prev) => ({
        status,
        message: message || prev.message,
        percent: prev.percent
      }));
    } catch (err) {
      setUpdateState({
        status: "error",
        message: err instanceof Error ? err.message : "Update check failed."
      });
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleInstallUpdate = async () => {
    if (!window.api?.installUpdate) return;
    await window.api.installUpdate();
  };

  const updateTone =
    updateState.status === "warning" || updateState.status === "skipped"
      ? "status-warning"
      : updateState.status === "error"
        ? "status-error"
        : updateState.status === "downloaded" || updateState.status === "none"
          ? "status-ok"
          : "label";

  const updateMessage = (() => {
    if (updateState.message) return updateState.message;
    if (!updatesSupported) return "Update checks are available in the desktop app only.";
    if (updateState.status === "idle") return "Run a manual check to see if updates are available.";
    if (updateState.status === "checking") return "Checking for updates...";
    if (updateState.status === "available") return "Update available. Preparing download...";
    if (updateState.status === "downloading") {
      const percent = typeof updateState.percent === "number" ? ` (${updateState.percent}%)` : "";
      return `Downloading update${percent}...`;
    }
    if (updateState.status === "downloaded") return "Update downloaded and ready to install.";
    if (updateState.status === "none") return "No updates available.";
    if (updateState.status === "skipped") return "Update check skipped in this build.";
    if (updateState.status === "warning") return "Update check failed. Verify the update server.";
    if (updateState.status === "error") return "Update check failed.";
    return "";
  })();

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h1>Settings</h1>
          <p>Manage NAS access, API endpoints, appearance, runtime, and updates.</p>
        </div>
        {appVersion && <span className="pill">v{appVersion}</span>}
      </div>

      <div className="settings-tabs" role="tablist" aria-label="Settings sections">
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className="pill settings-tab"
            id={`settings-tab-${tab.id}`}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`settings-panel-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid">
        {activeTab === "nas" && (
          <section
            className="card settings-card"
            id="settings-panel-nas"
            role="tabpanel"
            aria-labelledby="settings-tab-nas"
          >
            <h2 className="section-title">NAS Configuration</h2>
            <p style={{ color: "var(--text-subtle)" }}>
              Configure credentials and mount point used across modules. Changes persist locally.
            </p>
            <div className="grid two">
              <div>
                <div className="label">Username</div>
                <input
                  className="input"
                  required={false}
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
              </div>
              <div>
                <div className="label">Password</div>
                <input
                  className="input"
                  type="password"
                  required={false}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div>
                <div className="label">Server Path (e.g. 10.0.0.2/share)</div>
                <input
                  className="input"
                  required={false}
                  value={form.serverPath}
                  onChange={(e) => setForm({ ...form, serverPath: e.target.value })}
                />
              </div>
              <div>
                <div className="label">Mount point (required)</div>
                <input
                  className="input"
                  placeholder="/Volumes/data or Z:\\nas"
                  required
                  value={form.mountPoint}
                  onChange={(e) => setForm({ ...form, mountPoint: e.target.value })}
                />
                <div className="label" style={{ marginTop: 4 }}>
                  If the mount point is not currently mounted, you will be prompted for credentials. On
                  Windows, use a drive letter (e.g. Z:).
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
              <button className="button" onClick={handleSave} disabled={busy || missing}>
                {busy ? "Saving & Mounting…" : "Save & Mount"}
              </button>
              <button className="button secondary" onClick={handleClear}>
                Clear
              </button>
              <span className="pill">Status: {status}</span>
            </div>
            {mountPoint && <div className="label">Mounted at: {mountPoint}</div>}
            {error && <div className="status-error">{error}</div>}
          </section>
        )}

        {activeTab === "api" && (
          <section
            className="card settings-card"
            id="settings-panel-api"
            role="tabpanel"
            aria-labelledby="settings-tab-api"
          >
            <h2 className="section-title">PostgREST API</h2>
            <p style={{ color: "var(--text-subtle)" }}>
              Base URL used for Thermoimaging and Thickness data. Stored locally with a default of{" "}
              {DEFAULT_POSTGREST_URL}.
            </p>
            <div>
              <div className="label">PostgREST URL</div>
              <input
                className="input"
                placeholder={DEFAULT_POSTGREST_URL}
                value={postgrestUrl}
                onChange={(e) => setPostgrestUrl(e.target.value)}
              />
              <div className="label" style={{ marginTop: 6 }}>
                Requests will hit the root endpoint (`/`) when testing.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button className="button" onClick={handleSavePostgrest}>
                Save API URL
              </button>
              <button className="button secondary" onClick={handleTestConnection} disabled={testingApi}>
                {testingApi ? "Testing…" : "Test connection"}
              </button>
            </div>
            {testResult && (
              <div
                className={testResult.status === "error" ? "status-error" : "status-ok"}
                style={{ marginTop: 8 }}
              >
                {testResult.message}
              </div>
            )}
          </section>
        )}

        {activeTab === "appearance" && (
          <section
            className="card settings-card"
            id="settings-panel-appearance"
            role="tabpanel"
            aria-labelledby="settings-tab-appearance"
          >
            <h2 className="section-title">Appearance</h2>
            <p style={{ color: "var(--text-subtle)" }}>
              Choose the theme used across the interface. Changes apply immediately and persist locally.
            </p>
            <div>
              <div className="label">Theme</div>
              <select
                className="select"
                value={theme}
                onChange={(e) => handleThemeChange(e.target.value as AppTheme)}
              >
                <option value="default">ZeroCO2 (Current)</option>
                <option value="ipgr">IGPR Palette</option>
              </select>
            </div>
          </section>
        )}

        {activeTab === "runtime" && (
          <section
            className="card settings-card"
            id="settings-panel-runtime"
            role="tabpanel"
            aria-labelledby="settings-tab-runtime"
          >
            <h2 className="section-title">Runtime Diagnostics</h2>
            <p style={{ color: "var(--text-subtle)" }}>
              Confirms the bundled Python runtime and package sources used at startup.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button
                className="button secondary"
                onClick={loadRuntimeInfo}
                disabled={!runtimeSupported || loadingRuntime}
              >
                {loadingRuntime ? "Refreshing…" : "Refresh runtime status"}
              </button>
              {!runtimeSupported && (
                <span className="pill">Runtime diagnostics are available in the desktop app.</span>
              )}
            </div>

            {runtimeSupported && (
              <div className="grid two" style={{ marginTop: 12 }}>
                <div>
                  <div className="label">Startup status</div>
                  <div
                    className={
                      runtimeInfo?.success
                        ? "status-ok"
                        : runtimeInfo?.error
                          ? "status-error"
                          : "label"
                    }
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {runtimeInfo?.success ? "OK" : runtimeInfo?.error || "Unknown"}
                  </div>
                </div>
                <div>
                  <div className="label">Python version</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {runtimeInfo?.runtime?.pythonVersion || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Python source</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {runtimeInfo?.runtime?.pythonSource || "Unknown"}
                  </div>
                </div>
                <div>
                  <div className="label">Venv rebuilt</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {formatBool(runtimeInfo?.runtime?.venvRebuilt)}
                  </div>
                </div>
                <div>
                  <div className="label">Runtime path</div>
                  <div className="settings-path">
                    {runtimeInfo?.runtime?.runtimeDir || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Bundled Python binary</div>
                  <div className="settings-path">
                    {runtimeInfo?.runtime?.pythonBinary || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Wheelhouse path</div>
                  <div className="settings-path">
                    {runtimeInfo?.runtime?.wheelhouseDir || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Wheelhouse version</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {runtimeInfo?.runtime?.wheelhouseVersion || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Venv path</div>
                  <div className="settings-path">
                    {runtimeInfo?.runtime?.venvPath || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Venv Python</div>
                  <div className="settings-path">
                    {runtimeInfo?.runtime?.venvPython || "Unavailable"}
                  </div>
                </div>
                <div>
                  <div className="label">Wheelhouse used</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {formatBool(runtimeInfo?.runtime?.usedWheelhouse)}
                  </div>
                </div>
                <div>
                  <div className="label">Vendor fallback used</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {formatBool(runtimeInfo?.runtime?.usedVendorFallback)}
                  </div>
                </div>
              </div>
            )}

            {runtimeSupported && runtimeInfo?.runtime?.packages?.length ? (
              <div style={{ marginTop: 16 }}>
                <div className="label">Package status</div>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Package</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runtimeInfo.runtime.packages.map((pkg) => (
                      <tr key={pkg}>
                        <td>{pkg}</td>
                        <td>{formatPackageStatus(runtimeInfo.runtime?.packageStatus?.[pkg])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>
        )}

        {activeTab === "updates" && (
          <section
            className="card settings-card"
            id="settings-panel-updates"
            role="tabpanel"
            aria-labelledby="settings-tab-updates"
          >
            <h2 className="section-title">Updates</h2>
            <p style={{ color: "var(--text-subtle)" }}>
              Manually check for new releases and install them when ready.
            </p>
            <div>
              <div className="label">Current version</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {appVersion ? `v${appVersion}` : "Unknown"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button
                className="button"
                onClick={handleCheckUpdates}
                disabled={checkingUpdates || !updatesSupported}
              >
                {checkingUpdates ? "Checking…" : "Check for updates"}
              </button>
              {updateState.status === "downloaded" && updatesSupported && (
                <button className="button secondary" onClick={handleInstallUpdate}>
                  Restart to install
                </button>
              )}
            </div>
            {updateMessage && (
              <div className={updateTone} style={{ marginTop: 8 }}>
                {updateMessage}
              </div>
            )}
          </section>
        )}

        {activeTab === "notes" && (
          <section
            className="card settings-card"
            id="settings-panel-notes"
            role="tabpanel"
            aria-labelledby="settings-tab-notes"
          >
            <h2 className="section-title">Notes</h2>
            <ul>
              <li>Settings are stored locally and used at startup for auto-mount.</li>
              <li>PostgREST URL is configured here and saved locally (default {DEFAULT_POSTGREST_URL}).</li>
              <li>Leave Server Path blank and set an existing mount to skip SMB mounting.</li>
              <li>Theme changes apply immediately and persist locally.</li>
              <li>Applies to Thermoimaging and Thickness Plotter when available.</li>
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
